import React from "react";
import { cn } from "@/lib/utils";
import { Eraser } from "lucide-react";

interface FormSection {
    id: string;
    icon: React.ReactNode;
    label: string;
    content: React.ReactNode;
    required?: boolean;
    /** The label sits above and the content takes the full width (the medicines table needs the room) */
    block?: boolean;
}

interface FormSectionWrapperProps {
    section: FormSection;
    hasErrors: boolean;
    /** Empties the whole section; offered only while it has something in it */
    onClear?: () => void;
}

// ─── Expanded ─────────────────────────────────────────────────────────────────

const ExpandedSection = ({
    section, hasErrors, onClear,
}: {
    section: FormSection;
    hasErrors: boolean;
    /** Empties the whole section; offered only while it has something in it */
    onClear?: () => void;
}) => (
    <div className={cn("py-[7px]", hasErrors && "-mx-2 px-2 rounded bg-destructive/5")}>
        {/* As in the mockup: a 120px label column, 16px gap, sections 14px apart. Below 1280px the
            column narrows to 104px / 12px gap (still fits "Examination" + clear) so the fields get the room.
            Phones, and "block" sections (medicines): label + actions as a row above the content */}
        <div className={cn("flex flex-col", !section.block && "sm:flex-row sm:gap-3 xl:gap-4")}>

            {/* Left column — the label on the field's 36px line, and "clear section" once there is something to clear (templates live in the field itself) */}
            <div className={cn("w-full flex-shrink-0 pb-1.5 flex items-center",
                !section.block && "sm:w-[104px] xl:w-[120px] sm:pb-0 sm:min-h-9 sm:self-start")}>
                <h3 className={cn(
                    "text-[13px] font-semibold leading-tight truncate",
                    hasErrors ? "text-destructive" : "text-label"
                )}>
                    {section.label}
                    {section.required && <span className="text-destructive ml-0.5">*</span>}
                </h3>
                {onClear && (
                    <button
                        type="button"
                        onClick={onClear}
                        title={`Clear ${section.label.toLowerCase()}`}
                        aria-label={`Clear ${section.label.toLowerCase()}`}
                        className="touch-target ml-auto flex items-center justify-center w-6 h-6 rounded text-muted-foreground/50 hover:text-destructive hover:bg-accent flex-shrink-0"
                    >
                        <Eraser className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                {section.content}
            </div>
        </div>
    </div>
);

// ─── Export ───────────────────────────────────────────────────────────────────

export default function FormSectionWrapper({
    section, hasErrors, onClear,
}: FormSectionWrapperProps) {
    return (
        <ExpandedSection
            section={section}
            hasErrors={hasErrors}
            onClear={onClear}
        />
    );
}
